/**
 * Clone a blob: URL so the editor can keep it after the prep popup unmounts
 * and revokes its own object URLs.
 *
 * Non-blob URLs are returned unchanged.
 *
 * Prefer XHR over window.fetch — browser extensions often wrap fetch and break
 * blob: reads with "TypeError: Failed to fetch".
 */

function readBlobUrlViaXhr(url) {
  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", url, true);
      xhr.responseType = "blob";
      xhr.onload = () => {
        if (xhr.status === 200 || xhr.status === 0) {
          if (xhr.response instanceof Blob) {
            resolve(xhr.response);
            return;
          }
          reject(new Error("Blob URL did not return a Blob"));
          return;
        }
        reject(new Error(`Failed to read blob URL (${xhr.status})`));
      };
      xhr.onerror = () => reject(new Error("Failed to read blob URL"));
      xhr.onabort = () => reject(new Error("Blob URL read aborted"));
      xhr.send();
    } catch (err) {
      reject(err);
    }
  });
}

async function readBlobUrlViaFetch(url) {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error("Failed to adopt blob URL");
  }
  return res.blob();
}

/** Read bytes from a blob: URL without relying solely on window.fetch. */
export async function readBlobFromBlobUrl(url) {
  if (typeof url !== "string" || !url.startsWith("blob:")) {
    throw new Error("Not a blob URL");
  }

  try {
    return await readBlobUrlViaXhr(url);
  } catch (xhrErr) {
    try {
      return await readBlobUrlViaFetch(url);
    } catch (fetchErr) {
      const message =
        fetchErr?.message || xhrErr?.message || "Failed to read blob URL";
      const err = new Error(message);
      err.cause = fetchErr || xhrErr;
      throw err;
    }
  }
}

export async function adoptBlobUrl(url) {
  if (typeof url !== "string" || !url.startsWith("blob:")) {
    return url;
  }

  try {
    const blob = await readBlobFromBlobUrl(url);
    return URL.createObjectURL(blob);
  } catch (err) {
    // Revoked URL or extension-blocked read — keep original if still usable.
    console.warn("[adoptBlobUrl] clone failed; keeping original URL", err);
    return url;
  }
}
